import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import {
  ChartDateFilterLabels,
  IChartDateFilter,
  IItemBrandPricingComparative,
  IItemBrandPricingComparisonHistory,
} from '@/types/api/pricingsComparativeRegion';
import { ONE_DAY_IN_SECONDS } from '@/utils/dateUtils';
import { handleErrorAsync } from '@/utils/handlers';
import { ScriptableContext, Tick } from 'chart.js';

export interface IGetComparativeDateProps {
  item_id?: number | null;
  brand_id?: number | null;
  location_id?: number | null;
  time_range?: number | null;
}

export function setPointRadius(ctx: ScriptableContext<'line'>) {
  const { dataIndex, chart, datasetIndex, parsed } = ctx;

  if (!chart.data.datasets[datasetIndex].data.length) return 0;

  return dataIndex === chart.data.datasets[datasetIndex].data.length - 1
    ? 6
    : chart.scales.x.ticks.some(tick => tick.value === parsed.x)
    ? 3
    : 0;
}

export function formatTicks(
  value: string | number,
  index: number,
  values: Tick[],
  chartLabels: Array<string | undefined>
) {
  if (chartLabels.length <= 7) return chartLabels[index];

  const isLastValue = index === values.length - 1;

  if (isLastValue) return chartLabels[index];

  return Number(value) % (values.length / 6) === 0 ? chartLabels[index] : null;
}

export function generateChartData(
  comparative: IItemBrandPricingComparisonHistory[] = []
) {
  return comparative.reduce(
    (acc, data) => {
      if (!data) return acc;

      acc.average.push(data.average_price_micros / 1000000);
      acc.lower.push(data.minimum_price_micros / 1000000);
      return acc;
    },
    { average: [], lower: [] } as { average: number[]; lower: number[] }
  );
}

export function generateLabels(
  comparative: IItemBrandPricingComparisonHistory[] = []
) {
  return comparative?.map(data =>
    new Date(data.compared_at).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  );
}

export function generateComparativeList<T extends { id: number | null }>(
  list: T[] = [],
  itemSelected: Nullable<T>
) {
  return list.map(item => ({
    ...item,
    selected: itemSelected?.id === item?.id,
  }));
}

export async function getComparativeData(props: IGetComparativeDateProps = {}) {
  const { item_id, brand_id, location_id, time_range } = props;

  const generateUrl = () => {
    let url = '/api/item_brands/comparative/?';

    if (item_id) url += `item=${item_id}`;
    if (brand_id) url += `&brand=${brand_id}`;
    if (location_id) url += `&shipping_location=${location_id}`;
    if (time_range) url += `&time_range=${time_range}`;

    return url;
  };

  const [comparative, error] = await handleErrorAsync(async () => {
    const { data } = await api.get<IItemBrandPricingComparative>(generateUrl());

    return data;
  });

  if (error) {
    Toast('error', error.message);
    return;
  }

  return comparative;
}

const THIRTY_DAYS = 30;

export const dateFilters: IChartDateFilter[] = [
  {
    name: ChartDateFilterLabels.SevenDays,
    valueInSeconds: 7 * ONE_DAY_IN_SECONDS,
  },
  {
    name: ChartDateFilterLabels.ThirtyDays,
    valueInSeconds: THIRTY_DAYS * ONE_DAY_IN_SECONDS,
  },
  {
    name: ChartDateFilterLabels.ThreeMonths,
    valueInSeconds: 3 * THIRTY_DAYS * ONE_DAY_IN_SECONDS,
  },
  {
    name: ChartDateFilterLabels.SixMonths,
    valueInSeconds: 6 * THIRTY_DAYS * ONE_DAY_IN_SECONDS,
  },
];
